import { useQuery, useMutation } from '@apollo/client/react'
import { GET_RUN, GET_RUNS, GET_RUNS_STATUS, START_CAMPAIGN, STOP_RUN } from '@/lib/operations/runs'
import type { BuyerJourneyStage, RunDetail, RunSummary, RunSummaryGql, StartCampaignResult } from '@/lib/types'

// ─── Read hooks ───────────────────────────────────────────────────────────────

export function useRun(clientId: string, runId: string) {
  const { data, loading, error, startPolling, stopPolling, refetch } = useQuery<{ run: RunDetail }>(GET_RUN, {
    variables: { clientId, runId },
    skip: !clientId || !runId,
  })
  return {
    run: data?.run,
    loading,
    error,
    startPolling,
    stopPolling,
    refetch,
  }
}

export function useListRuns(clientId: string) {
  const { data, loading, error } = useQuery<{ client: { runs: RunSummaryGql[] } }>(GET_RUNS, {
    variables: { clientId },
    skip: !clientId,
  })
  return { runs: data?.client?.runs ?? [], loading, error }
}

export function useRunsStatus(runIds: string[]) {
  const { data, loading, error } = useQuery<{ runsStatus: RunSummary[] }>(GET_RUNS_STATUS, {
    variables: { runIds },
    skip: !runIds.length,
  })
  return {
    runs: data?.runsStatus ?? [],
    loading,
    error,
  }
}

// ─── Mutation hooks ───────────────────────────────────────────────────────────

export function useStartCampaign() {
  const [mutate, { loading }] = useMutation<{ startCampaign: StartCampaignResult }>(START_CAMPAIGN)
  const startCampaign = (
    clientId: string,
    selectedContentTypes?: string[],
    engine?: string,
    buyerJourneyStages?: BuyerJourneyStage[],
  ) =>
    mutate({ variables: { clientId, selectedContentTypes, engine, buyerJourneyStages } })
  return { startCampaign, loading }
}

export function useStopRun() {
  const [mutate, { loading }] = useMutation<{ stopRun: boolean }>(STOP_RUN)
  const stopRun = (clientId: string, runId: string) =>
    mutate({ variables: { clientId, runId } })
  return { stopRun, loading }
}
